import { useEffect, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { red } from "@mui/material/colors";

import { withTranslation, useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import bomVersionService from "@plannel/services/data/bom-version-service";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import { AddButton, CopyButton, RemoveButton } from "@plannel/components/ActionIconButton";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

const ITEM_SEARCH_FIELDS = ["itemCd"];

/* 본문에 ag-grid 를 품는 설정 모달의 폭. 프리셋(lg = 500)으로는 컬럼이 잘려 예외로 숫자를 준다.
   본문이 DialogContent px:2 안이라 예전 Box 폭(1000) + 32 를 줘야 같은 폭이 나온다
   (CalendarGroupModal 의 932 = 900 + 32 와 같은 계산). */
const MODAL_WIDTH = 1032;

/**
 * 기간별 BOM 버전(z_bom_version) 관리 모달.
 * <p>
 * 버전은 자기 소유의 BOM 행을 갖지 않는다. 완제품과 하위 구성품의 z_bom 행이 모두 같은
 * bom_ver_id 를 물고 있어 버전 하나가 BOM 데이터 세트 전체를 가리킨다. 그래서
 * - 복사는 원본 버전의 z_bom 행을 새 bom_ver_id 로 일괄 복제하고(하위 구성품까지),
 * - 삭제는 헤더와 동반 z_bom 행을 함께 지운다.
 * bom_ver_id = 0 인 기준 BOM 행은 버전의 유효 기간이 덮지 않는 구간에 적용되는 별개의 데이터 세트다.
 * 버전을 써도 남아 있는 것이 정상이므로 배너로 어떤 구간에 쓰이는지 안내만 한다.
 *
 * onClose(changed) 의 changed 가 true 면 호출한 화면이 버전 목록·그리드를 다시 읽어야 한다.
 */
const BomVersionModal = ({ open, itemCd, onClose }) => {
  const { t } = useTranslation();

  const gridRef = useRef();
  const searchCode = useRef(itemCd ?? "");
  const changedRef = useRef(false);

  const [bomVersions, setBomVersions] = useState([]);
  const [baseBomRowCnt, setBaseBomRowCnt] = useState(0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [copyDialog, setCopyDialog] = useState({ open: false, source: null });
  const [copyForm, setCopyForm] = useState({ bomVerCd: "", effStartDt: null, effEndDt: null, descTxt: "" });
  const [copyBomRows, setCopyBomRows] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rows: [] });

  const searchItemCd = () => searchCode.current?.trim() ?? "";

  const getItemName = code => bomVersions.find(bomVersion => bomVersion.itemCd === code)?.itemName ?? null;

  const isNewRow = params => !params.data?.id;

  const dateFormatter = params => dateUtils.formatDate(params.value, "yyyy-MM-dd");

  const describeVersion = bomVersion => {
    if (!bomVersion) {
      return "";
    }

    return `${bomVersion.itemCd} / ${bomVersion.bomVerCd}  ${dateUtils.formatDate(bomVersion.effStartDt, "yyyy-MM-dd")} ~ ${dateUtils.formatDate(bomVersion.effEndDt, "yyyy-MM-dd")}`;
  };

  const bomRowCntCellStyle = params => {
    return params.value > 0 ? {} : { color: red[600] };
  };

  const columnDefs = [
    { headerName: "itemCd", field: "itemCd", cellClass: "stringType", width: 130, editable: isNewRow },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 160 },
    { headerName: "bomVersion", field: "bomVerCd", colId: "bomVersion", width: 120 },
    {
      headerName: "effStartDt", field: "effStartDt", type: ["defaultValueParser"],
      cellEditor: GridDatePicker, cellEditorParams: { dateType: "day", popupGrid: true },
      width: 120, valueFormatter: dateFormatter
    },
    {
      headerName: "effEndDt", field: "effEndDt", type: ["defaultValueParser"],
      cellEditor: GridDatePicker, cellEditorParams: { dateType: "day", popupGrid: true },
      width: 120, valueFormatter: dateFormatter
    },
    {
      headerName: "bomRowCnt", field: "bomRowCnt", type: ["numberColumn", "nonEditableColumn"],
      width: 110, cellStyle: bomRowCntCellStyle
    },
    { headerName: "descTxt", field: "descTxt", width: 240 },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 80 },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 150 }
  ];

  const showErrorSnack = (e, fallbackKey) => {
    const message = e.response?.data?.message;
    if (!message?.message) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(fallbackKey) });
      debug(e);
      return;
    }

    const { detail } = message;
    const detailText = detail ? (t(detail) === detail ? detail : t(detail)) : "";
    const content = detailText ? `${t(message.message)} (${detailText})` : t(message.message);

    setSnackInfo({ open: true, severity: message.type ?? snackBarMessageColor.saveFail, content });
  };

  const retrieveBomVersions = (code = searchItemCd()) => {
    const params = { searchFilters: { itemCd: code } };

    return bomVersionService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setBomVersions(results);
        setBaseBomRowCnt(response.data.baseBomRowCnt ?? 0);
      })
      .catch(e => {
        setBomVersions([]);
        showErrorSnack(e, "MSG_ServerError");
      })
      .then(() => {
        DataState.initialize(gridRef.current?.api);
      });
  };

  const resolveItemCd = selected => {
    if (!selected) {
      return "";
    }

    if (typeof selected === "string") {
      return selected.trim();
    }

    const matched = selected[selected.groupKey] ?? Object.entries(selected).find(([key, value]) => key !== "groupKey" && value)?.[1];

    return matched ? String(matched).trim() : "";
  };

  const selectItem = selected => {
    const code = resolveItemCd(selected);

    searchCode.current = code;
    retrieveBomVersions(code);
  };

  const addRow = () => {
    const code = searchItemCd();
    const newRow = {
      id: null,
      itemCd: code || null,
      itemName: code ? getItemName(code) : null,
      bomVerCd: null,
      effStartDt: null,
      effEndDt: null,
      descTxt: null,
      activeFlg: true,
      bomRowCnt: 0,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const saveBomVersions = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (isEmpty(allStateData)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    bomVersionService.upsert(allStateData)
      .then(() => {
        changedRef.current = true;
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        retrieveBomVersions();
      })
      .catch(e => {
        showErrorSnack(e, "MSG_SaveFailed");
      });
  };

  const openCopyDialog = () => {
    const api = gridRef.current.api;
    api.stopEditing();

    const selectedRows = api.getSelectedRows().filter(row => row.id);
    if (isEmpty(selectedRows)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.validation, content: t("MSG_NoSelectedVersion") });
      return;
    }

    const source = selectedRows[0];
    const sourceEndDt = new ZDate(source.effEndDt);
    const nextStartDt = dateUtils.dateValidation(sourceEndDt) && sourceEndDt.getFullYear() < 9999
      ? new ZDate(sourceEndDt.getFullYear(), sourceEndDt.getMonth(), sourceEndDt.getDate() + 1)
      : null;

    setCopyForm({ bomVerCd: "", effStartDt: nextStartDt, effEndDt: null, descTxt: "" });
    setCopyBomRows(true);
    setCopyDialog({ open: true, source });
  };

  const copyBomVersion = () => {
    const { source } = copyDialog;
    if (!source) {
      return;
    }

    const data = {
      srcBomVerId: source.id,
      bomVerCd: copyForm.bomVerCd,
      effStartDt: dateUtils.formatDate(copyForm.effStartDt, "yyyy-MM-dd") || null,
      effEndDt: dateUtils.formatDate(copyForm.effEndDt, "yyyy-MM-dd") || null,
      descTxt: copyForm.descTxt,
      activeFlg: true,
      copyBomRows
    };

    bomVersionService.copy(data)
      .then(response => {
        const copied = response.data?.data;
        changedRef.current = true;
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.saveSuccess,
          content: t("MSG_BomVersionCopySuccess", {
            source: source.bomVerCd,
            target: copied?.bomVerCd ?? data.bomVerCd,
            count: copied?.bomRowCnt ?? 0
          })
        });
        retrieveBomVersions();
      })
      .catch(e => {
        showErrorSnack(e, "MSG_SaveFailed");
      });
  };

  const openDeleteDialog = () => {
    const api = gridRef.current.api;
    api.stopEditing();

    const selectedNodes = api.getSelectedNodes();
    if (isEmpty(selectedNodes)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.validation, content: t("MSG_NoSelectedRow") });
      return;
    }

    /* 미저장 신규 행은 서버에 없으므로 그리드에서만 걷어낸다. */
    const newNodes = selectedNodes.filter(node => !node.data?.id);
    if (!isEmpty(newNodes)) {
      DataState.deleteState(api, newNodes);
    }

    const savedRows = selectedNodes.filter(node => node.data?.id).map(node => node.data);
    if (isEmpty(savedRows)) {
      return;
    }

    setDeleteDialog({ open: true, rows: savedRows });
  };

  const removeBomVersions = () => {
    const ids = deleteDialog.rows.map(row => row.id);
    if (isEmpty(ids)) {
      return;
    }

    bomVersionService.remove(ids)
      .then(response => {
        changedRef.current = true;
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.saveSuccess,
          content: t("MSG_BomVerDeleteSuccess", { count: ids.length, rowCount: response.data?.deletedBomRowCnt ?? 0 })
        });
        retrieveBomVersions();
      })
      .catch(e => {
        showErrorSnack(e, "MSG_DeleteFailed");
      });
  };

  const handleClose = () => {
    onClose(changedRef.current);
  };

  useEffect(() => {
    if (open) {
      retrieveBomVersions();
    }

    return () => {
      setBomVersions([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const deletedBomRowCnt = deleteDialog.rows.reduce((sum, row) => sum + (row.bomRowCnt ?? 0), 0);

  const copyDialogContent = (
    <Stack spacing={1.5} sx={{ mt: 0.5 }}>
      <Box sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "grey.50" }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("sourceBomVersion")}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Typography sx={{ fontSize: 13 }}>{describeVersion(copyDialog.source)}</Typography>
          <Chip size="small" variant="outlined" label={`${t("bomRowCnt")} ${copyDialog.source?.bomRowCnt ?? 0}`} sx={{ fontSize: 11 }} />
        </Box>
      </Box>
      <TextField
        required
        size="small"
        label={t("newBomVerCd")}
        value={copyForm.bomVerCd}
        onChange={e => setCopyForm({ ...copyForm, bomVerCd: e.target.value })}
      />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <DatePicker
            label={t("effStartDt")}
            views={["year", "month", "day"]}
            format="yyyy-MM-dd"
            value={copyForm.effStartDt}
            onChange={value => setCopyForm({ ...copyForm, effStartDt: value })}
            slotProps={{ textField: { size: "small", required: true, sx: { width: 180 } } }}
          />
          <DatePicker
            label={t("effEndDt")}
            views={["year", "month", "day"]}
            format="yyyy-MM-dd"
            value={copyForm.effEndDt}
            minDate={copyForm.effStartDt ?? undefined}
            onChange={value => setCopyForm({ ...copyForm, effEndDt: value })}
            slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
          />
        </Box>
      </LocalizationProvider>
      <TextField
        size="small"
        label={t("descTxt")}
        value={copyForm.descTxt}
        onChange={e => setCopyForm({ ...copyForm, descTxt: e.target.value })}
      />
      {/* 하위 구성품 행도 같은 bom_ver_id 를 달고 있어 한 번에 복제된다. 기준 BOM 행은 다른 구간(버전이
          덮지 않는 구간)의 데이터 세트라 복제 대상이 아니다. */}
      <Alert severity="info" sx={{ fontSize: 12, py: 0, "& .MuiAlert-message": { fontSize: 12 } }}>
        {t("MSG_BomVersionCopyHint")}
      </Alert>
    </Stack>
  );

  const deleteDialogContent = (
    <Stack spacing={1} sx={{ mt: 0.5 }}>
      <Typography sx={{ fontSize: 14 }}>{t("MSG_BomVerDeleteConfirm", { count: deleteDialog.rows.length })}</Typography>
      {deletedBomRowCnt > 0 && (
        <Alert severity="warning" sx={{ "& .MuiAlert-message": { fontSize: 12 } }}>
          {t("MSG_BomVerDeleteBomRows", { count: deletedBomRowCnt })}
        </Alert>
      )}
      <Box>
        {deleteDialog.rows.map(row => (
          <Typography key={row.id} sx={{ fontSize: 12, color: "text.secondary" }}>{describeVersion(row)}</Typography>
        ))}
      </Box>
    </Stack>
  );

  return (
    <>
      {/* 저장은 z_bom_version 을 실제로 upsert 하므로 셸 기본값(t("save") + SaveIcon)을 그대로 쓴다.
          닫기는 부모 화면에 변경 여부를 알려 목록을 다시 읽게 하므로 취소가 아니라 close 다. */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("bomVersionMgmt")}
        onSave={saveBomVersions}
        cancelLabel={t("close")}
        width={MODAL_WIDTH}
      >
        {/* 툴바 — 검색은 왼쪽, 행 조작은 오른쪽. 셸이 본문에 16px 을 주므로 여기에 mt 를 또 주지 않는다. */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* 다른 화면과 같은 품목 검색(Elasticsearch 자동완성). 후보에서 고르면 그 품목의 버전만
              조회되고 [추가] 시 신규 행의 품목 코드도 그 값으로 채워진다(행에서 직접 입력·수정 가능). */}
          <ItemAutocomplete
            retrieve={retrieveBomVersions}
            setSelectedItem={selectItem}
            searchFields={ITEM_SEARCH_FIELDS}
            searchValue={searchCode}
            useAccessFilter={false}
            sx={{ width: 260, mr: 0 }}
          />
          <Box sx={{ ml: "auto" }}>
            <AddButton onClick={addRow} />
            <RemoveButton onClick={openDeleteDialog} />
            <CopyButton label="copyBomVersion" onClick={openCopyDialog} />
          </Box>
        </Box>
        {baseBomRowCnt > 0 && (
          <Alert severity="info" sx={{ mt: 1, py: 0, "& .MuiAlert-message": { fontSize: 12 } }}>
            {t("MSG_BaseBomRowsRemain", { count: baseBomRowCnt })}
          </Alert>
        )}
        <Box className="ag-theme-balham" sx={{ height: "40vh" }} mt={1}>
          <AgGridReact
            ref={gridRef}
            rowData={bomVersions}
            columnDefs={GridUtils.getColumnDefs(columnDefs)}
            {...DefaultGridSetting({})}
            rowSelection={{
              mode: "multiRow",
              selectAll: "filtered",
              checkboxes: true,
              headerCheckbox: true,
              enableClickSelection: true
            }}
          />
        </Box>
      </BaseConfigModal>
      <Dialog
        open={copyDialog.open}
        onClose={() => setCopyDialog({ open: false, source: null })}
        title="copyBomVersion"
        actionName="copy"
        content={copyDialogContent}
        checkBox={{ checkBox: true, checkBoxMessage: t("copyBomRows") }}
        checked={copyBomRows}
        setChecked={setCopyBomRows}
        maxWidth="sm"
        onHandler={action => {
          if (action === "COPY") {
            copyBomVersion();
          }
        }}
      />
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, rows: [] })}
        title="delete"
        content={deleteDialogContent}
        maxWidth="sm"
        onHandler={action => {
          if (action === "DELETE") {
            removeBomVersions();
          }
        }}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(BomVersionModal);
